/**
 * Account restore: bringing back a member who deleted their account and is
 * still inside their thirty-day grace period.
 *
 * The deletion flow works - it soft-deletes the profile, opens the window and
 * schedules the hard delete - but nothing has ever been able to reverse it,
 * while the deletion notification tells the member to get in touch if they
 * change their mind. This surface is the operator's side of that promise.
 *
 * Two places: a banner at the top of `/consumers/:id`, because a member inside
 * a grace period explains every other empty panel on that page, and a list at
 * `/account-deletions` for finding those members without knowing who to look
 * for. Both read through `restore-service`, which now calls the real routes.
 */

import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { Button } from '@ui/components/base/buttons/button';
import { Callout, FailureText } from '@ui/console/primitives';
import { Term } from '@ui/console/glossary';
import { formatCount, plural } from '@ui/console/measures';
import { MetricBand } from '@ui/console/MetricBand';
import type { Tone } from '@ui/console/status';
import { RecordTable } from './record-table';
import { IdentifierCopy } from './references';
import { SelectField, TextAreaField } from '@ui/console/forms';
import { Absent, InsightState, useInsight } from './consumer-insights';
import { Actions, DetailList, Page, Pager, Status } from './components';
import {
  listDeletionRequests,
  readMemberDeletionRequest,
  remainingGrace,
  restorability,
  restoreAccount,
  type DeletionRequest,
  type DeletionRequestPage,
  type Restorability,
} from './restore-service';
import type { DeletionRequestLookup } from '@closet-theory/api';

const PAGE = 25;
const ACCOUNT_RESTORE_REASON = 'Restored a deleted account from the account recovery console.';

function when(value: string | null | undefined, fallback = 'Not recorded') {
  return value ? new Date(value).toLocaleString() : fallback;
}

/** Why a request cannot be restored, said plainly instead of shown as a dead button. */
const BLOCKED: Record<string, { title: string; body: string }> = {
  completed: {
    title: 'The grace period has passed and the hard delete has run.',
    body: 'This account was permanently deleted, so there is nothing left to restore. Nothing in this console can undo it — the member has to sign up again as a new member.',
  },
  already_restored: {
    title: 'This account has already been restored.',
    body: 'The deletion request was reversed, so the member is active again and the scheduled hard delete no longer applies. No further action is needed here.',
  },
  expired: {
    title: 'The grace period has passed, so this account can no longer be restored.',
    body: 'The window closed and the hard delete is due or already done. Offering a Restore button here would be offering something that cannot work.',
  },
  unreadable: {
    title: 'This request does not carry a readable grace period.',
    body: 'The record does not say when its grace period ends, so this console cannot tell whether restoring is still safe and will not offer the action. Open the row itself to see what it does carry.',
  },
};

function RestoreFailure({ detail }: { detail: string }) {
  return (
    <Callout tone="error" title="The account was not restored.">
      <FailureText raw={detail} />
      <span className="block pt-1">
        The member is still deleted and the hard delete is still scheduled. Nothing was retried
        automatically. A second restore of an already-restored request is refused rather than
        applied twice, so re-read the record before trying again.
      </span>
    </Callout>
  );
}

/**
 * The action itself: never one click. The operator is shown what restoring
 * does, gives a reason, and then confirms.
 */
function RestoreControl({
  request,
  onRestored,
}: {
  request: DeletionRequest;
  onRestored: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState(ACCOUNT_RESTORE_REASON);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();
  const [done, setDone] = useState(false);

  if (done)
    return (
      <Callout tone="success" title="This account has been restored.">
        The grace period is cleared and the scheduled hard delete no longer applies. The member can
        sign in again.
      </Callout>
    );

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setFailure(undefined);
    const result = await restoreAccount(request.request_id, {
      reason: reason.trim() || ACCOUNT_RESTORE_REASON,
    });
    setBusy(false);
    if (result.error || !result.data) {
      // A failed restore must never read as a restore. No retry, no fallback.
      setFailure(
        typeof result.error === 'string'
          ? result.error
          : ((result.error as { detail?: string } | undefined)?.detail ?? 'request_failed'),
      );
      return;
    }
    setDone(true);
    onRestored();
  };

  if (!confirming)
    return (
      <div className="flex flex-col gap-3">
        {failure ? <RestoreFailure detail={failure} /> : null}
        <Actions>
          <Button size="md" color="primary" onClick={() => setConfirming(true)}>
            Restore account
          </Button>
        </Actions>
      </div>
    );

  return (
    <form
      onSubmit={(event) => void submit(event)}
      className="flex flex-col gap-4 rounded-xl border border-secondary bg-primary p-5"
    >
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold text-primary">Confirm restore</h3>
        <p className="text-sm text-tertiary">
          Restoring clears the deletion request, cancels the scheduled hard delete and makes the
          member active again with their wardrobe, profile and history intact. It does not notify
          them — tell them yourself. This is recorded against your operator account.
        </p>
      </div>
      <TextAreaField
        label="Reason for restoring this account"
        name="reason"
        rows={3}
        maxLength={500}
        hint="Prefilled for the audit log. Add detail when it will help the next operator."
        value={reason}
        onChange={(event) => setReason(event.currentTarget.value)}
      />
      {failure ? <RestoreFailure detail={failure} /> : null}
      <Actions>
        <Button
          size="md"
          color="secondary"
          onClick={() => {
            setConfirming(false);
            setFailure(undefined);
          }}
        >
          Cancel
        </Button>
        <Button size="md" color="primary" type="submit" isDisabled={busy}>
          {busy ? 'Restoring…' : 'Confirm restore'}
        </Button>
      </Actions>
    </form>
  );
}

function DeletionDetail({ request }: { request: DeletionRequest }) {
  const verdict = restorability(request);
  return (
    <DetailList
      items={[
        ['Status', <Status value={request.status} />],
        ['Member asked to be deleted', when(request.requested_at)],
        [
          <>
            <Term term="grace period" /> ends
          </>,
          when(request.hard_delete_after),
        ],
        [
          'Time left to restore',
          verdict.restorable ? remainingGrace(verdict.expiresAt) : 'None — the window has closed',
        ],
        ...(request.restored_at || request.status === 'restored'
          ? ([['Restored', when(request.restored_at, 'Not recorded')]] as Array<
              [ReactNode, ReactNode]
            >)
          : []),
        ...(request.completed_at || request.status === 'completed'
          ? ([
              [
                <>
                  <Term term="hard delete" /> has run
                </>,
                when(request.completed_at, 'Not recorded'),
              ],
            ] as Array<[ReactNode, ReactNode]>)
          : []),
        [
          'Deletion request reference',
          <IdentifierCopy label="Deletion request" value={request.request_id} />,
        ],
      ]}
    />
  );
}

/**
 * What the banner says, and how loudly.
 *
 * This used to be one ternary: restorable, or "deleted and the grace period is
 * over" in red. A restored account fell into the second branch, so a member who
 * was brought back read as an alarm whose headline contradicted the status,
 * the dates and the panel directly beneath it. Every outcome that still warns
 * now gets its own sentence and its own tone.
 *
 * A restored account reaches none of these: the caller returns before here,
 * because the banner's job is to explain panels an operator would otherwise
 * misread, and a restored member's panels are full.
 */
function bannerHeadline(verdict: Restorability): { tone: Tone; title: string; body: ReactNode } {
  // The soft-delete explanation belongs only where the profile actually is
  // soft-deleted. A restored member's panels are full, so telling them their
  // profile is hidden would be the same contradiction in a quieter voice.
  const stillHidden = (
    <>
      Their profile is <Term term="soft delete">soft-deleted</Term>, which is why the panels below
      read as empty. That is this deletion record showing through, not a fault in those reads.
    </>
  );
  if (verdict.restorable)
    return {
      tone: 'warning',
      title: `This member deleted their account. ${remainingGrace(verdict.expiresAt)} left in their grace period.`,
      body: stillHidden,
    };
  if (verdict.reason === 'unreadable')
    return {
      tone: 'warning',
      title: 'This member deleted their account and the grace period cannot be read.',
      body: (
        <>
          The deletion is real but its expiry is unreadable, so nothing here can tell you whether
          restoring is still possible. Treat the dates below as unverified.
        </>
      ),
    };
  return {
    tone: 'error',
    title: 'This member deleted their account and the grace period is over.',
    body: (
      <>
        Their profile is <Term term="soft delete">soft-deleted</Term> or already gone, which is why
        the panels below read as empty. That is this deletion record showing through, not a fault in
        those reads.
      </>
    ),
  };
}

/**
 * The banner on `/consumers/:id`. Deliberately above the tabs rather than
 * inside one: an operator must see a deleted account before they read anything
 * else about the member.
 *
 * It renders `useInsight`'s states itself rather than through `InsightState`,
 * because it sits above a page that already renders that shell per tab. It
 * stays silent while loading, disappears entirely for the ordinary member who
 * never deleted anything, and words its own failure so it is never mistaken
 * for the panel below it failing.
 */
export function MemberDeletionBanner({ id }: { id: string }) {
  const source = useInsight<DeletionRequestLookup>(() => readMemberDeletionRequest(id), [id]);
  const state = source.state;
  if (state.status === 'loading') return null;
  if (state.status === 'forbidden')
    return (
      <Callout tone="warning" title="Your role cannot read this member's deletion request.">
        This is a permission boundary, not a member who is fine — if this member deleted their
        account, this page will not say so. Ask an admin to widen your access.
      </Callout>
    );
  if (state.status === 'failed')
    return (
      <Callout
        tone="warning"
        title="This member's deletion state could not be read."
        actions={
          <Button size="sm" color="secondary" onClick={() => void source.refresh()}>
            Check again
          </Button>
        }
      >
        <FailureText raw={state.detail} />
        <span className="block pt-1">
          Treat this page as unknown rather than safe: nothing here can tell you whether this member
          is inside a deletion grace period.
        </span>
      </Callout>
    );

  const request = state.data.request;
  if (!request) return null;
  const verdict = restorability(request);
  // A restored account is not a condition, it is history. The banner exists to
  // stop an operator reading a deleted member's empty panels as a fault; a
  // member who was brought back has full panels and nothing to warn about, so
  // the banner was spending the top of the page saying everything is fine.
  // The deletion record is still on the account restore screen for anyone who
  // needs the dates.
  if (!verdict.restorable && verdict.reason === 'already_restored') return null;
  const blocked = verdict.restorable ? undefined : BLOCKED[verdict.reason];
  const headline = bannerHeadline(verdict);
  return (
    <Callout tone={headline.tone} title={headline.title}>
      <div className="flex flex-col gap-4 pt-1">
        <p>{headline.body}</p>
        <DeletionDetail request={request} />
        {verdict.restorable ? (
          <RestoreControl request={request} onRestored={() => void source.refresh()} />
        ) : blocked ? (
          <Callout tone="neutral" title={blocked.title}>
            {blocked.body}
          </Callout>
        ) : null}
      </div>
    </Callout>
  );
}

/* --------------------------------------------------------------------- list */

/** Lifecycle words are the row's own; "All" is always first and always default. */
const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'soft_deleted', label: 'Soft deleted' },
  { value: 'restored', label: 'Restored' },
  { value: 'accepted', label: 'Accepted for deletion' },
  { value: 'quiescing', label: 'Quiescing' },
  { value: 'processing', label: 'Processing' },
  { value: 'retryable_failure', label: 'Failed, can be retried' },
  { value: 'manual_review', label: 'Needs manual review' },
  { value: 'completed', label: 'Completed' },
];

export function AccountDeletions() {
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const offset = Number(params.get('offset') ?? 0);
  const source = useInsight<DeletionRequestPage>(
    () => listDeletionRequests({ limit: PAGE, offset, ...(status ? { status } : {}) }),
    [status, offset],
  );
  const filter = (next: { status?: string; offset?: number }) =>
    setParams({
      ...((next.status ?? status) ? { status: next.status ?? status } : {}),
      offset: String(next.offset ?? 0),
    });
  const ready = source.state.status === 'ready' ? source.state.data : undefined;
  // Both figures come from the server over every request on record, whatever
  // the status filter, so they never depend on which page is on screen.
  const restorable = ready?.restorable_count ?? 0;
  // The nearest deadline is the only figure on this screen that is a clock
  // rather than a count, and it is the one that decides what to do first.
  const soonest = ready?.next_hard_delete_after
    ? new Date(ready.next_hard_delete_after).getTime()
    : undefined;
  return (
    <Page
      title="Account deletions"
      purpose="Use this page to find members who deleted their account and are still inside the thirty days during which it can be brought back. Restoring itself is done from the member's own page."
      band={
        <MetricBand
          label="Who can still be brought back"
          figures={[
            {
              id: 'requests',
              label: 'Deletion requests matching this status',
              ...(source.state.status === 'ready'
                ? {
                    value: formatCount(source.state.data.total),
                    unit: plural(source.state.data.total, 'request'),
                    raw: source.state.data.total,
                  }
                : { absent: 'The deletion list has not come back yet.' }),
              hint: 'Counted by the server over every request on record, not over the page on screen.',
            },
            {
              id: 'restorable',
              label: (
                <>
                  Still inside the <Term term="grace period" />
                </>
              ),
              ...(ready
                ? {
                    value: formatCount(restorable),
                    unit: plural(restorable, 'member'),
                    raw: restorable,
                  }
                : { absent: 'The deletion list has not come back yet.' }),
              hint: 'Every member who can still be brought back, from the member’s own page. They lead the list, soonest deadline first.',
            },
            {
              id: 'soonest',
              label: 'Nearest deadline',
              ...(!ready
                ? { absent: 'The deletion list has not come back yet.' }
                : soonest === undefined || Number.isNaN(soonest)
                  ? {
                      absent:
                        'Nobody is inside a grace period, so there is no deadline to be nearest.',
                    }
                  : {
                      value: remainingGrace(new Date(soonest)),
                      raw: new Date(soonest).toISOString(),
                      tone: soonest - Date.now() < 3 * 86_400_000 ? ('error' as const) : undefined,
                      statusLabel:
                        soonest - Date.now() < 3 * 86_400_000 ? 'decide this week' : undefined,
                      hint: (
                        <>
                          Time left before the next <Term term="hard delete" /> runs. After it,
                          restoring is impossible.
                        </>
                      ),
                    }),
            },
          ]}
        />
      }
      toolbar={{
        onRefresh: () => void source.refresh(),
        filters: (
          <SelectField
            label="Status"
            value={status}
            onChange={(event) => filter({ status: event.currentTarget.value, offset: 0 })}
            options={STATUS_OPTIONS}
          />
        ),
      }}
    >
      <InsightState source={source} subject="open deletion requests">
        {(page) =>
          page.items.length ? (
            <div className="flex flex-col gap-3">
              <RecordTable
                noun="deletion requests"
                records={page.items}
                columns={['member_id', 'status', 'hard_delete_after', 'requested_at']}
                rowActions={(record) => {
                  const memberId = record['member_id'];
                  if (typeof memberId !== 'string') return null;
                  return (
                    <Button size="sm" color="secondary" href={`/consumers/${memberId}`}>
                      Open member
                    </Button>
                  );
                }}
              />
              <Pager
                offset={page.offset}
                limit={page.limit}
                total={page.total}
                onPage={(next) => filter({ offset: next })}
              />
            </div>
          ) : status ? (
            <Absent title={`No deletion request has status “${status}”.`}>
              Choose “All statuses” to see every deletion request on record.
            </Absent>
          ) : (
            <Absent title="No member is inside a deletion grace period.">
              Nothing is pending a hard delete, so there is no one to restore.
            </Absent>
          )
        }
      </InsightState>
    </Page>
  );
}
